// PlayersView.tsx
// Created by Taulant Geci

import React, { useState, useEffect } from 'react';
import { MatchGeneratorController } from '../modules/MatchGeneratorController';
import { Player, PlayerGroup, SkillLevel, AppState } from '../models/types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faUserPlus, 
  faPlus, 
  faTrash, 
  faUser, 
  faUserSlash, 
  faToggleOn, 
  faToggleOff,
  faFolder,
  faUserGroup,
  faUserGraduate,
  faUserTie,
  faTrophy,
  faQuestionCircle,
  faChevronDown,
  faChevronUp,
  faInfoCircle,
  faUserCheck,
  faExclamationTriangle,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';

interface PlayersViewProps {
  controller: MatchGeneratorController;
}

export const PlayersView: React.FC<PlayersViewProps> = ({ controller }) => {
  const { t } = useTranslation();
  const [state, setState] = useState(controller.getState());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [newPlayerName, setNewPlayerName] = useState('');
  const [selectedGroupForNewPlayer, setSelectedGroupForNewPlayer] = useState('');

  useEffect(() => {
    const unsubscribe = controller.subscribe(setState);
    return unsubscribe;
  }, [controller]);

  const handleCreateGroup = () => {
    if (controller.createPlayerGroup(state.newGroupName)) {
      // Group created successfully
    }
  };

  const handleDeleteGroup = (groupId: string) => {
    controller.deletePlayerGroup(groupId);
  };

  const handleAddPlayerToGroup = () => {
    if (newPlayerName.trim() && selectedGroupForNewPlayer) {
      // First add the player to allPlayers
      if (controller.addPlayer(newPlayerName.trim())) {
        // Get the newly added player ID (last added)
        const newPlayer = state.allPlayers[state.allPlayers.length - 1];
        if (newPlayer) {
          controller.addPlayerToGroup(newPlayer.id, selectedGroupForNewPlayer);
        }
      }
      setNewPlayerName('');
    }
  };

  const handleTogglePlayerInGroup = (playerId: string, groupId: string) => {
    if (controller.isPlayerInGroup(playerId, groupId)) {
      controller.removePlayerFromGroup(playerId, groupId);
    } else {
      controller.addPlayerToGroup(playerId, groupId);
    }
  };

  const handleUpdatePlayerSkill = (playerId: string, skillLevel: SkillLevel) => {
    controller.updatePlayerSkill(playerId, skillLevel);
  };

  const handleRemovePlayer = (playerId: string) => {
    controller.removePlayer(playerId);
  };

  const getSkillLevelIcon = (skillLevel: SkillLevel) => {
    switch (skillLevel) {
      case SkillLevel.NOT_SET:
        return faQuestionCircle;
      case SkillLevel.BEGINNER:
        return faUser;
      case SkillLevel.INTERMEDIATE:
        return faUsers;
      case SkillLevel.ADVANCED:
        return faTrophy;
      default:
        return faQuestionCircle;
    }
  };

  const getSkillLevelColor = (skillLevel: SkillLevel) => {
    switch (skillLevel) {
      case SkillLevel.NOT_SET:
        return '#6c757d'; // gray
      case SkillLevel.BEGINNER:
        return '#0dcaf0'; // info blue
      case SkillLevel.INTERMEDIATE:
        return '#ffc107'; // warning yellow
      case SkillLevel.ADVANCED:
        return '#198754'; // success green
      default:
        return '#6c757d';
    }
  };

  const toggleGroupExpansion = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  return (
    <div className="players-view">
      {/* Header */}
      <div className="view-header">
        <h1 className="view-title">{t('playerManagement')}</h1>
      </div>

      <div className="main-content">
        {/* Player Groups Section */}
        <div className="section">
          <div className="section-header">
            <div className="section-title">
              <FontAwesomeIcon icon={faUserGroup} className="section-icon" />
              {t('playerGroups')}
            </div>
          </div>

          <div className="card-ios">
            {/* Create Group */}
            <div className="input-group mb-3">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faUsers} />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder={t('newGroupName') || 'Neuer Gruppenname...'}
                value={state.newGroupName}
                onChange={(e) => controller.setNewGroupName(e.target.value)}
              />
              <button
                className="btn btn-primary"
                onClick={handleCreateGroup}
                disabled={!state.newGroupName.trim()}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>

            {/* Groups List */}
            {state.playerGroups.length === 0 ? (
              <div className="empty-state">
                <FontAwesomeIcon icon={faUserSlash} className="empty-icon" />
                <p>{t('noGroupsCreated')}</p>
              </div>
            ) : (
              <div className="groups-list">
                {state.playerGroups.map(group => (
                  <div key={group.id} className="group-item">
                    <div 
                      className="group-header"
                      onClick={() => toggleGroupExpansion(group.id)}
                    >
                      <div className="group-info">
                        <FontAwesomeIcon icon={faFolder} className="group-icon" />
                        <span className="group-name">{group.name}</span>
                        <span className="group-count">
                          {controller.getPlayersInGroup(group.id).length} {t('players')}
                        </span>
                      </div>
                      <div className="group-actions">
                        <button
                          className="btn-icon btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteGroup(group.id);
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                        <FontAwesomeIcon 
                          icon={expandedGroups.has(group.id) ? faChevronUp : faChevronDown}
                          className="expand-icon"
                        />
                      </div>
                    </div>

                    {/* Expanded Group Content */}
                    {expandedGroups.has(group.id) && (
                      <div className="group-content">
                        {state.allPlayers.length === 0 ? (
                          <div className="group-empty">
                            <FontAwesomeIcon icon={faUserSlash} />
                            <span>{t('noPlayersAdded')}</span>
                          </div>
                        ) : (
                          <>
                            <div className="group-description">
                              <FontAwesomeIcon icon={faInfoCircle} />
                              <span>{t('assignPlayersTo')} "{group.name}":</span>
                            </div>
                            <div className="group-players">
                              {state.allPlayers.map(player => (
                                <div key={player.id} className="group-player-toggle">
                                  <div className="player-info">
                                    <FontAwesomeIcon icon={faUser} />
                                    <span>{player.name}</span>
                                  </div>
                                  <button
                                    className={`toggle-switch ${controller.isPlayerInGroup(player.id, group.id) ? 'active' : ''}`}
                                    onClick={() => handleTogglePlayerInGroup(player.id, group.id)}
                                  >
                                    <FontAwesomeIcon 
                                      icon={controller.isPlayerInGroup(player.id, group.id) ? faToggleOn : faToggleOff}
                                    />
                                  </button>
                                </div>
                              ))}
                            </div>

                            {/* Players in Group Info */}
                            {controller.getPlayersInGroup(group.id).length > 0 && (
                              <div className="group-players-info">
                                <FontAwesomeIcon icon={faInfoCircle} />
                                <span>
                                  {t('playersInGroup')}: {controller.getPlayersInGroup(group.id).map(p => p.name).join(', ')}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Add Player to Group Section */}
        <div className="section">
          <div className="section-header">
            <div className="section-title">
              <FontAwesomeIcon icon={faUserPlus} className="section-icon" />
              {t('addPlayerToGroup')}
            </div>
          </div>

          <div className="card-ios">
            {state.playerGroups.length === 0 ? (
              <div className="warning-message">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{t('createGroupFirst')}</span>
              </div>
            ) : (
              <>
                {/* Group Selection */}
                <div className="input-group mb-3">
                  <span className="input-group-text">
                    <FontAwesomeIcon icon={faFolder} />
                  </span>
                  <select
                    className="form-control"
                    value={selectedGroupForNewPlayer}
                    onChange={(e) => setSelectedGroupForNewPlayer(e.target.value)}
                  >
                    <option value="">{t('selectGroup')}</option>
                    {state.playerGroups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Player Name Input */}
                <div className="input-group mb-3">
                  <span className="input-group-text">
                    <FontAwesomeIcon icon={faUserPlus} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('newPlayerName') || 'Neuer Spielername'}
                    value={newPlayerName}
                    onChange={(e) => setNewPlayerName(e.target.value)}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleAddPlayerToGroup}
                    disabled={!newPlayerName.trim() || !selectedGroupForNewPlayer}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Player Skill Levels Section */}
        <div className="section">
          <div className="section-header">
            <div className="section-title">
              <FontAwesomeIcon icon={faTrophy} className="section-icon" />
              {t('playerSkillLevels')}
            </div>
          </div>

          <div className="card-ios">
            {state.allPlayers.length === 0 ? (
              <div className="empty-state">
                <FontAwesomeIcon icon={faUserSlash} className="empty-icon" />
                <p>{t('noPlayersAdded')}</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-dark table-hover mb-0">
                  <thead>
                    <tr>
                      <th scope="col" className="border-0" style={{width: '35%'}}>
                        <FontAwesomeIcon icon={faUser} className="me-2" />
                        {t('players')}
                      </th>
                      <th scope="col" className="border-0 text-center" style={{width: '45%'}}>
                        <FontAwesomeIcon icon={faTrophy} className="me-2" />
                        {t('level')}
                      </th>
                      <th scope="col" className="border-0 text-end" style={{width: '20%'}}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.allPlayers.map(player => (
                      <tr key={player.id}>
                        <td className="border-0">
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faUser} className="player-icon me-2" />
                            <span className="player-name">{player.name}</span>
                          </div>
                        </td>
                        <td className="border-0 text-center">
                          <div className="skill-level-container d-flex align-items-center justify-content-center">
                            <FontAwesomeIcon 
                              icon={getSkillLevelIcon(player.skillLevel)} 
                              className="skill-icon me-2"
                              style={{ color: getSkillLevelColor(player.skillLevel) }}
                              title={`${t('skillLevel')}: ${player.skillLevel}`}
                            />
                            <select
                              value={player.skillLevel}
                              onChange={(e) => handleUpdatePlayerSkill(player.id, e.target.value as SkillLevel)}
                              className="form-select-dark skill-select"
                            >
                              <option value={SkillLevel.NOT_SET}>{t('notSet')}</option>
                              <option value={SkillLevel.BEGINNER}>{t('beginner')}</option>
                              <option value={SkillLevel.INTERMEDIATE}>{t('intermediate')}</option>
                              <option value={SkillLevel.ADVANCED}>{t('advanced')}</option>
                            </select>
                          </div>
                        </td>
                        <td className="border-0 text-end">
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleRemovePlayer(player.id)}
                            title={t('removePlayer') as string}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 